import { getItemAttributeValueByIdentifier } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/AttributeSelectors';
import {
  HUE_GATE_DEGREES,
  HUE_GATE_LIGHT_DEGREES,
  LIGHT_MAX_DARKEN,
  LIGHT_TARGET_L,
  SATURATION_FLOOR,
} from '../constants/svgColorAreas';
import { AttributeIdentifiers } from '../Enums';
import { formatHexColor } from './editorUnits';
import {
  deltaE2000,
  hueDistanceDegrees,
  labHueDegrees,
  parseCssColorToRgb,
  rgbToHsl,
  type Hsl,
  type Rgb,
} from './colorDistance';
import type { ColorArea } from '../Reducers/Configurator/types';

export type ColorOptionLike = {
  identifier?: string;
};

type OptionWithRgb = {
  option: ColorOptionLike;
  rgb: Rgb;
  hsl: Hsl;
  labHue: number;
};

function optionHex(option: ColorOptionLike): string | null {
  if (!option.identifier) return null;
  const hex = getItemAttributeValueByIdentifier(option, AttributeIdentifiers.hex, '');
  return hex ? formatHexColor(hex) : null;
}

function optionRgb(option: ColorOptionLike): Rgb | null {
  return parseCssColorToRgb(optionHex(option));
}

function collectOptionsWithRgb(options: ColorOptionLike[]): OptionWithRgb[] {
  const result: OptionWithRgb[] = [];
  options.forEach((option) => {
    if (!option.identifier) return;
    const rgb = optionRgb(option);
    if (!rgb) return;
    result.push({ option, rgb, hsl: rgbToHsl(rgb), labHue: labHueDegrees(rgb) });
  });
  return result;
}

function pickClosestByDeltaE2000(target: Rgb, candidates: OptionWithRgb[]): ColorOptionLike | null {
  let bestOption: ColorOptionLike | null = null;
  let bestDistance = Infinity;
  candidates.forEach(({ option, rgb }) => {
    const distance = deltaE2000(target, rgb);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestOption = option;
    }
  });
  return bestOption;
}

/**
 * Union of HSL and LAB hue gates: HSL handles pastel pinks; LAB handles chartreuse
 * (yellow–green) where HSL distances are misleading.
 */
function filterByHueGates(
  pool: OptionWithRgb[],
  targetHsl: Hsl,
  targetLabHue: number,
  gateDegrees: number
): OptionWithRgb[] {
  const seen = new Set<ColorOptionLike>();
  const result: OptionWithRgb[] = [];
  pool.forEach((entry) => {
    const inHsl = hueDistanceDegrees(targetHsl.h, entry.hsl.h) <= gateDegrees;
    const inLab = hueDistanceDegrees(targetLabHue, entry.labHue) <= gateDegrees;
    if (!inHsl && !inLab) return;
    if (seen.has(entry.option)) return;
    seen.add(entry.option);
    result.push(entry);
  });
  return result;
}

/**
 * Map a detected paint hex to the closest product color option.
 * Chromatic targets: prefer options within HSL or LAB hue gates, then min CIEDE2000.
 * Light/pastel targets (L ≥ LIGHT_TARGET_L): also exclude much darker options and use a
 * slightly wider hue gate so pastel neighbors (e.g. Hellrosa) can compete.
 * Neutral targets (S < SATURATION_FLOOR): match among low-saturation options first.
 * Falls back to global CIEDE2000 when the preferred candidate set is empty.
 */
export function mapHexToClosestColorOption(
  hex: string,
  options: ColorOptionLike[]
): ColorArea | null {
  const target = parseCssColorToRgb(hex);
  if (!target) return null;

  const withRgb = collectOptionsWithRgb(options);
  if (!withRgb.length) return null;

  const targetHsl = rgbToHsl(target);
  const targetLabHue = labHueDegrees(target);
  let pool = withRgb;
  let hueGate = HUE_GATE_DEGREES;

  if (targetHsl.l >= LIGHT_TARGET_L) {
    const lightPool = withRgb.filter(({ hsl }) => hsl.l >= targetHsl.l - LIGHT_MAX_DARKEN);
    if (lightPool.length) {
      pool = lightPool;
      hueGate = HUE_GATE_LIGHT_DEGREES;
    }
  }

  let candidates: OptionWithRgb[];

  if (targetHsl.s < SATURATION_FLOOR) {
    candidates = pool.filter(({ hsl }) => hsl.s < SATURATION_FLOOR);
  } else {
    candidates = filterByHueGates(pool, targetHsl, targetLabHue, hueGate);
  }

  const bestOption = pickClosestByDeltaE2000(
    target,
    candidates.length ? candidates : withRgb
  );
  if (!bestOption?.identifier) return null;

  const mappedHex = optionHex(bestOption) || formatHexColor(hex);
  return { color: mappedHex, colorOptionId: bestOption.identifier };
}

export function mapDetectedColorsToOptions(
  detectedHexes: string[],
  options: ColorOptionLike[]
): ColorArea[] {
  return detectedHexes.map((hex) => {
    return (
      mapHexToClosestColorOption(hex, options) || {
        color: formatHexColor(hex),
        colorOptionId: options[0]?.identifier || 'black',
      }
    );
  });
}
